/**
 * Spanish copy, for the funnel that faces the Spanish market: the public demo
 * and the registration.
 *
 * Same wording rules as en.js: describe the data, never promise a result,
 * never imply suitability or regulatory approval. Spanish adds one trap worth
 * naming — "test de idoneidad" is a regulated MiFID II assessment, so no
 * string here may suggest the platform performs it or replaces it.
 *
 * A key missing from this file falls back to English rather than breaking the
 * page, so a partial translation is safe to ship.
 */
const es = {
    'common.language': 'Idioma',
    'common.language.en': 'English',
    'common.language.es': 'Español',
    'common.syntheticBadge': 'Datos sintéticos',
    'common.notAvailable': 'no disponible',
    'common.missing': 'falta',

    'nav.plans': 'Funciones y precios',
    'nav.login': 'Entrar',
    'footer.rights': '© {year} FinanSee. Todos los derechos reservados.',
    'footer.support': 'Soporte:',
    'footer.legal.privacy': 'Política de Privacidad',
    'footer.legal.terms': 'Condiciones de uso',
    'footer.legal.cookies': 'Política de cookies',
    'footer.legal.disclaimer': 'Aviso legal',
    'footer.dpa': 'Plantilla de DPA a petición ->',

    // --- Demostración pública ---------------------------------------------
    'demo.meta.title': 'Demostración pública — revisar una cartera de clientes | FinanSee',
    'demo.meta.description': 'Recorre la revisión de una cartera de clientes sobre el libro sintético de un despacho español: lista de revisión, ficha del cliente e informe. Sin registro y sin datos de tus clientes.',

    'demo.hero.badge': 'Demostración pública · datos sintéticos',
    'demo.hero.title': 'Mira cómo se revisa una cartera de clientes, sin registrarte.',
    'demo.hero.subtitle': 'Este es el libro sintético de un despacho independiente: {clients} clientes y un asesor. Recorre las tres pantallas donde está el trabajo real: la lista de revisión, la ficha del cliente y el informe.',
    'demo.hero.point.noSignup': 'Sin registro y sin correo',
    'demo.hero.point.noData': 'Sin datos de tus clientes',
    'demo.hero.point.oneMinute': 'Menos de un minuto',
    'demo.hero.cta': 'Abrir la lista de revisión',

    'demo.disclosure.title': 'Todo lo de esta página es sintético',
    'demo.disclosure.body': 'Los clientes, los nombres, los importes y las fechas se escribieron para esta demostración. Nada proviene de un cliente, una entidad o una serie de mercado real, y ninguna cifra es un resultado. Con tus datos, estas mismas pantallas muestran tu cartera.',

    'demo.book.title': 'El libro que cargó este despacho',
    'demo.book.file': 'Archivo',
    'demo.book.rows': 'Clientes en el extracto',
    'demo.book.assets': 'Patrimonio asesorado',
    'demo.book.median': 'Cliente mediano',
    'demo.book.coverage': 'Cobertura de evidencia',
    'demo.book.coverageHint': 'Proporción de los campos que necesita el orden de revisión y que este extracto rellena.',
    'demo.book.observed': 'Fecha del dato',
    'demo.book.imported': 'Fecha de carga',
    'demo.book.undated': 'Clientes sin fecha de origen',
    'demo.book.undatedHint': 'Se muestran como sin fecha; nunca se dan por actuales.',
    'demo.book.notRankable': 'Clientes que no se pueden ordenar',
    'demo.book.notRankableHint': 'La evidencia es demasiado escasa o antigua para compararla con el resto del libro.',
    'demo.book.firm': 'Despacho independiente, {city} · {advisers} asesor, {analysts} analista',

    'demo.steps.one': 'Lista de revisión',
    'demo.steps.two': 'Ficha del cliente',
    'demo.steps.three': 'Informe de revisión',

    'demo.queue.title': '1. Los clientes a revisar',
    'demo.queue.subtitle': 'Orden de revisión dentro de este libro. No es una medida de riesgo ni una probabilidad.',
    'demo.queue.showing': 'Se muestran {shown} ejemplos de un libro sintético de {total} clientes, incluidos casos a verificar.',
    'demo.queue.col.client': 'Cliente',
    'demo.queue.col.order': 'Orden',
    'demo.queue.col.reason': 'Por qué está en la lista',
    'demo.queue.col.evidence': 'Evidencia',
    'demo.queue.col.observed': 'Fecha del dato',
    'demo.queue.col.adviser': 'Asesor',
    'demo.queue.open': 'Abrir ficha',
    'demo.queue.openAria': 'Abrir la ficha de {name}',
    'demo.queue.undated': 'Sin fecha en el extracto',
    'demo.queue.coverage': '{value}% de los campos',

    'demo.guide.title': 'Cómo leer esta lista',
    'demo.guide.order': 'Las puntuaciones de esta demo son entradas sintéticas ilustrativas. Las prioridades usan las reglas del motor sobre el libro completo; no es un cálculo completo del perfil de clientes ni una medida de riesgo.',
    'demo.guide.labels': 'Los límites usan los {total} clientes del libro completo, no solo los {ranked} con evidencia utilizable. En este ejemplo hay {high} prioridades Altas y {medium} Medias. Una puntuación inferior a {minimum} no entra en ninguno de esos grupos.',
    'demo.guide.low': 'Los demás clientes con evidencia utilizable siguen en revisión ordinaria. Los {notRankable} clientes sin evidencia utilizable se mantienen aparte para verificar, sin una puntuación accionable.',
    'demo.guide.reference': 'Ejemplo sintético fijo evaluado a fecha de {date}, no a fecha de hoy.',
    'demo.guide.missing': 'Lo que falta sigue faltando. Nunca cuenta como un cero ni se convierte en una señal.',
    'demo.guide.change': 'La variación en el tiempo necesita dos extractos con fecha. Este es el primero, así que todavía no se muestra ninguna tendencia.',

    'demo.urgency.high': 'Alta',
    'demo.urgency.medium': 'Media',
    'demo.urgency.low': 'Baja',
    'demo.urgency.verify': 'Verificar',
    'demo.priority.unscored': 'Sin prioridad',

    'demo.client.title': '2. Ficha del cliente',
    'demo.client.subtitle': 'Lo que el extracto contiene sobre {name}, y lo que no.',
    'demo.client.facts': 'Del extracto',
    'demo.client.signals': 'Señales observadas',
    'demo.client.missing': 'No está en el extracto',
    'demo.client.missingEmpty': 'Para este cliente están todos los campos que necesita el orden de revisión.',
    'demo.client.nextStep': 'Siguiente paso de revisión',
    'demo.client.limits': 'La ficha describe los datos cargados. No evalúa la idoneidad, no puntúa al cliente como persona y no anticipa lo que hará. El asesor decide si procede una conversación.',
    'demo.client.field.id': 'Identificador',
    'demo.client.field.country': 'País',
    'demo.client.field.age': 'Edad',
    'demo.client.field.risk': 'Perfil de riesgo declarado',
    'demo.client.field.knowledge': 'Puntuación de conocimiento',
    'demo.client.field.experience': 'Años de experiencia',
    'demo.client.field.portfolio': 'Valor de la cartera',
    'demo.client.field.allocation': 'Distribución',
    'demo.client.field.home': 'Peso del mercado doméstico',
    'demo.client.field.drift': 'Desvío respecto al modelo',
    'demo.client.field.leverage': 'Apalancamiento',
    'demo.client.field.coverage': 'Cobertura de evidencia',
    'demo.client.field.observed': 'Fecha del dato',
    'demo.client.value.risk': '{value}/10',
    'demo.client.value.knowledge': '{value}/100',
    'demo.client.value.years': '{value} años',
    'demo.client.value.drift': '{value} puntos',
    'demo.client.value.leverage': '{value}x',
    'demo.client.value.allocation': 'liquidez {cash}% · fondos y ETF {funds}% · acciones individuales {stocks}%',
    'demo.client.value.percent': '{value}%',

    'demo.report.title': '3. Informe de revisión',
    'demo.report.subtitle': 'La hoja que queda archivada tras revisar a {name}.',
    'demo.report.print': 'Imprimir o guardar en PDF',
    'demo.report.section.sources': 'Fuentes y fechas',
    'demo.report.section.coverage': 'Cobertura',
    'demo.report.section.indicators': 'Indicadores observados',
    'demo.report.section.verify': 'A verificar antes de la conversación',
    'demo.report.section.limits': 'Lo que este informe no dice',
    'demo.report.sources.body': 'Fuente única: {file}, cargado el {imported}. Fecha del dato: {observed}. {rows} filas, {columns} columnas.',
    'demo.report.sources.undated': 'Fuente única: {file}, cargado el {imported}. Este cliente no traía fecha de observación, así que nada de lo que sigue puede darse por actual hasta confirmarlo.',
    'demo.report.coverage.body': 'Para este cliente está presente el {value}% de los campos que necesita el orden de revisión. El resto aparece más abajo como ausente y no se ha estimado.',
    'demo.report.verify.empty': 'No se ha detectado ningún campo ausente para este cliente. Confirmar la fecha del dato con la ficha del cliente sigue siendo buena práctica.',
    'demo.report.limits.body': 'Este informe no afirma un resultado ni anticipa el comportamiento del cliente. No mide la rentabilidad de la cartera, no sustituye el test de idoneidad ni la evaluación de conveniencia, y no otorga ninguna aprobación regulatoria. Recoge lo que mostraban los datos cargados en la fecha indicada, lo que faltaba y el paso de revisión que eligió el asesor.',
    'demo.report.footer': 'Ejemplo sintético generado por la demostración pública de FinanSee. No es un documento de cliente.',

    'demo.cta.title': 'Haz esto con tu propia cartera',
    'demo.cta.body': 'La prueba de 30 días funciona sobre un extracto CSV o XLSX de tus clientes. La carga revisa cada columna antes de guardar nada, y los campos que falten siguen visibles como ausentes.',
    'demo.cta.primary': 'Abrir la prueba de 30 días',
    'demo.cta.secondary': 'Descargar una muestra lista para la prueba (120 clientes)',
    'demo.cta.note': '30 días, sin tarjeta y sin conversión automática. Dos campos para abrir la cuenta: correo y contraseña.',

    'demo.firm.city': 'Madrid',
    'demo.adviser.lead': 'Álvaro Nieto',
    'demo.adviser.analyst': 'Clara Sanchís',

    'demo.signal.single_stock_concentration': 'Concentración en un solo valor: {value}% de la cartera',
    'demo.signal.home_bias': 'Exposición al mercado doméstico: {value}%',
    'demo.signal.cash_drag': 'Liquidez sin asignar: {value}%',
    'demo.signal.risk_above_knowledge': 'Riesgo declarado {risk}/10 con una puntuación de conocimiento de {knowledge}/100',
    'demo.signal.allocation_drift': 'Desvío respecto al modelo de referencia: {value} puntos',
    'demo.signal.leverage': 'Apalancamiento registrado: {value}x',
    'demo.signal.no_rebalance': 'Sin reequilibrio registrado en {value} meses',
    'demo.signal.recent_switch': '{value} cambios de fondo en los últimos 90 días',

    'demo.missing.missing_source_date': 'La fecha de observación del dato',
    'demo.missing.missing_knowledge': 'La puntuación de conocimiento',
    'demo.missing.missing_experience': 'Los años de experiencia',
    'demo.missing.missing_experience_detail': 'Qué abarca la experiencia registrada',
    'demo.missing.missing_mandate': 'El mandato o modelo de referencia con el que comparar',
    'demo.missing.stale_evidence': 'Un extracto reciente: el dato más nuevo es del {observed}',

    'demo.reason.low_risk_high_concentration': 'El riesgo declarado es conservador y la cartera está concentrada',
    'demo.reason.declared_risk_vs_knowledge': 'El riesgo declarado es alto y la puntuación de conocimiento es baja',
    'demo.reason.drift_without_date': 'Hay desvío y el dato no tiene fecha',
    'demo.reason.concentration_with_leverage': 'Concentración y apalancamiento registrado a la vez',
    'demo.reason.cash_without_mandate': 'Posición de liquidez alta sin un mandato con el que compararla',
    'demo.reason.stale_evidence_with_drift': 'Desvío medido sobre datos de hace meses',
    'demo.reason.concentration_single_name': 'Concentración en una sola posición',
    'demo.reason.frequent_switching': 'Cambios frecuentes en poco tiempo',
    'demo.reason.home_concentration': 'Casi toda la cartera está en el mercado doméstico',
    'demo.reason.incomplete_profile': 'El perfil está incompleto para ordenarse',

    'demo.nextStep.confirm_mandate': 'Confirmar el mandato que consta en el expediente y revisar la concentración frente a él.',
    'demo.nextStep.document_rationale': 'Registrar el motivo del posicionamiento actual antes de la próxima instrucción.',
    'demo.nextStep.verify_source_then_review': 'Confirmar la fecha del dato con la ficha del cliente antes de dar por actual cualquier señal.',
    'demo.nextStep.request_missing_fields': 'Pedir a administración los campos que faltan y un extracto actualizado.',
    'demo.nextStep.schedule_review_call': 'Agendar una conversación de revisión y preparar las cifras anteriores.',

    // --- Registro ----------------------------------------------------------
    'register.meta.title': 'Crea tu cuenta — Plataforma FinanSee para asesores',
    'register.meta.description': 'Abre un espacio de trabajo en FinanSee con un correo y una contraseña. La prueba de 30 días funciona sobre tu propio extracto CSV o XLSX.',
    'register.badge': 'Dos campos',
    'register.title': 'Crea tu cuenta con un correo y una contraseña.',
    'register.subtitle': 'Es todo lo que pedimos ahora. Tu nombre y los datos del despacho los pedimos después, desde dentro, cuando ya hayas visto qué hace.',
    'register.step.one.title': 'Correo y contraseña',
    'register.step.one.description': 'Dos campos. Nada sobre tus clientes.',
    'register.step.two.title': 'Confirma el correo',
    'register.step.two.description': 'Un código de 6 dígitos, normalmente en menos de 30 segundos.',
    'register.step.three.title': 'Mira por dentro',
    'register.step.three.description': 'El espacio abre con un libro sintético. Cargas tu extracto cuando quieras.',
    'register.form.title': 'Abre tu espacio de trabajo',
    'register.form.email': 'Correo electrónico',
    'register.form.emailHint': 'Lo mejor es un correo de trabajo, porque será el propietario del espacio. Cualquier dirección sirve.',
    'register.form.emailPlaceholder': 'tu@tudespacho.com',
    'register.form.password': 'Contraseña',
    'register.form.passwordPlaceholder': 'Al menos 10 caracteres',
    'register.form.passwordHint': 'Al menos 10 caracteres. La longitud importa más que los símbolos, así que una frase corta funciona bien.',
    'register.form.passwordStrength': 'Seguridad: {label}.',
    'register.form.submit': 'Crear la cuenta',
    'register.form.submitting': 'Creando la cuenta...',
    'register.form.referral': 'Tengo un código de referencia',
    'register.form.referralPlaceholder': 'Código de socio o de referencia',
    'register.form.termsIntro': 'Al crear la cuenta aceptas nuestras:',
    'register.form.termsLink': 'Condiciones',
    'register.form.privacyLink': 'Política de Privacidad',
    'register.form.haveAccount': '¿Ya tienes cuenta?',
    'register.form.signIn': 'Entrar',
    'register.demoLink': '¿Prefieres mirar antes? Abre la demostración pública, sin cuenta.',
    'register.error.emailRequired': 'Escribe tu correo',
    'register.error.emailInvalid': 'Escribe una dirección de correo válida',
    'register.error.passwordRequired': 'Elige una contraseña',
    'register.error.passwordShort': 'Usa al menos 10 caracteres',
    'register.error.fixForm': 'Revisa el formulario',
    'register.password.weak': 'corta',
    'register.password.fair': 'correcta',
    'register.password.strong': 'alta',
    'register.toast.success': 'Cuenta creada',
    'register.toast.successBody': 'Te hemos enviado un código de verificación al correo.',

    // --- Perfil del espacio, se pide tras el primer acceso -----------------
    'profile.title': 'Dinos de quién es este espacio',
    'profile.body': 'No lo preguntamos al registrarte. Estos tres datos dan nombre a tu espacio y salen en los informes que exportes.',
    'profile.field.name': 'Tu nombre',
    'profile.field.business': 'Nombre del despacho',
    'profile.field.employees': 'Personas que lo usarán',
    'profile.employees.solo': 'Solo yo',
    'profile.employees.small': 'De 2 a 4',
    'profile.employees.medium': 'De 5 a 10',
    'profile.employees.large': 'Más de 10',
    'profile.submit': 'Guardar',
    'profile.later': 'Más tarde',
    'profile.saved': 'Guardado',
    'profile.error': 'No se ha podido guardar. Inténtalo de nuevo.',
    'profile.error.nameRequired': 'Escribe tu nombre',
    'profile.error.businessRequired': 'Escribe el nombre del despacho',
    'otp.badge': 'Verificación del correo',
    'otp.hero.title': 'Verifica tu correo antes de que se abra el espacio.',
    'otp.hero.body': 'Usamos un código de 6 dígitos para confirmar el correo del administrador. Una vez verificado puedes entrar y seguir con el plan, la carga de datos y la configuración del espacio.',
    'otp.next.title': 'Qué pasa ahora',
    'otp.next.one': 'Escribe el código que te ha llegado para activar la cuenta de administrador.',
    'otp.next.two': 'Si tarda más de 30 segundos, deja esta página abierta y pide que te lo reenviemos.',
    'otp.next.three': 'Al verificarlo volvemos al acceso con el mismo correo ya escrito.',
    'otp.card.title': 'Verifica tu correo',
    'otp.card.body': 'Escribe el código de 6 dígitos que enviamos al correo con el que te registraste.',
    'otp.alert.sending': 'Estamos enviando tu código. Deja esta página abierta mientras sale el correo.',
    'otp.alert.waiting': 'El código suele llegar en menos de 30 segundos. Mira en spam si pasados 2 minutos no está.',
    'otp.field.email': 'Correo electrónico',
    'otp.field.code': 'Código de verificación',
    'otp.field.codeHelp': 'Escribe el código de 6 dígitos de tu correo.',
    'otp.cta.verify': 'Verificar y continuar',
    'otp.cta.verifying': 'Verificando...',
    'otp.cta.resend': 'Reenviar el código',
    'otp.cta.resending': 'Enviando...',
    'otp.cta.back': 'Volver al registro',
    'otp.wrongEmail': '¿Correo equivocado?',
    'otp.restart': 'Empezar el registro de nuevo',
    'otp.toast.emailRequired.title': 'Falta el correo',
    'otp.toast.emailRequired.body': 'Escribe tu dirección de correo.',
    'otp.toast.verified.title': 'Verificación correcta',
    'otp.toast.verified.body': 'Correo verificado.',
    'otp.toast.failed.title': 'No se ha podido verificar',
    'otp.toast.failed.body': 'El código no es válido. Inténtalo otra vez.',
    'otp.toast.resent.title': 'Código reenviado',
    'otp.toast.resent.body': 'Hemos enviado un código nuevo a tu correo.',
    'otp.toast.resendFailed.title': 'No se ha podido reenviar',
    'otp.toast.resendFailed.body': 'No hemos podido reenviar el código. Inténtalo otra vez.',
    'login.badge': 'Acceso a la plataforma',
    'login.hero.title': 'Abre tu espacio FinanSee desde un único acceso seguro.',
    'login.after.title': 'Esto es lo que se abre al entrar',
    'login.after.queue.label': 'Cola de prioridad',
    'login.after.queue.value': 'A quién llamar hoy',
    'login.after.queue.meta': 'Espacio del gestor',
    'login.after.evidence.label': 'Revisión de evidencia',
    'login.after.evidence.value': 'Falta el rastro de idoneidad',
    'login.after.evidence.meta': 'Espacio de cumplimiento',
    'login.after.drift.label': 'Desvío de cartera',
    'login.after.drift.value': 'Desajuste de riesgo a la vista',
    'login.after.drift.meta': 'Espacio del asesor de carteras',
    'login.card.title': 'Entrar',
    'login.card.body': 'Tu espacio según el rol se abre después de identificarte.',
    'login.alert.session.title': 'La sesión anterior necesita atención',
    'login.alert.session.body': 'Vuelve a entrar para continuar.',
    'login.alert.verified.title': 'Correo verificado',
    'login.alert.verified.body': 'Entra con el mismo correo y la misma contraseña del registro.',
    'login.field.email': 'Correo electrónico',
    'login.field.password': 'Contraseña',
    'login.field.passwordPlaceholder': 'Escribe tu contraseña',
    'login.field.forgot': '¿La olvidaste?',
    'login.field.role': 'Rol',
    'login.field.adminEmail': 'Correo del administrador del despacho',
    'login.employee.toggle': 'Entro con un rol de empleado',
    'login.employee.help': 'Úsalo para credenciales de gestor, cumplimiento, asesor de carteras o dirección de asesoramiento creadas por el administrador del despacho.',
    'login.cta.submit': 'Entrar',
    'login.cta.loading': 'Comprobando el acceso...',
    'login.newHere': '¿Primera vez?',
    'login.register': 'Crea una cuenta de administrador',
    'login.role.rm': 'Gestor de clientes',
    'login.role.rm.desc': 'Revisa las carteras asignadas, la información del cliente, el orden de riesgo y las prioridades del día.',
    'login.role.portfolio': 'Asesor de carteras',
    'login.role.portfolio.desc': 'Abre la información del cliente y las vistas de referencia de cartera.',
    'login.role.compliance': 'Responsable de cumplimiento',
    'login.role.compliance.desc': 'Consulta la actividad de auditoría, los controles y las vistas orientadas a cumplimiento.',
    'login.role.head': 'Dirección de asesoramiento',
    'login.role.head.desc': 'Accede a los paneles de referencia y de dirección del asesoramiento.',
    'login.toast.ok.title': 'Has entrado',
    'login.toast.ok.body': 'Abriendo tu espacio.',
    'login.toast.ok.role': 'Abriendo tu espacio de {role}.',
    'login.toast.failed.title': 'No se ha podido entrar',
    'login.toast.failed.body': 'Credenciales no válidas.',
    'login.toast.error.title': 'Error',
    'login.toast.error.body': 'Algo ha fallado. Inténtalo otra vez.',
    'login.toast.offline': 'No hemos podido conectar con el servidor. Comprueba tu conexión.',
    'login.hero.body': 'Los titulares del espacio entran directamente. Quien trabaja en el despacho puede añadir el correo del administrador cuando necesita acceso como gestor, cumplimiento, asesor de carteras o dirección de asesoramiento.',
    'login.point.one': 'El espacio según el rol se abre al entrar',
    'login.point.two': 'Acceso de administrador y gestor en Team',
    'login.point.three': 'Roles de Business para equipos especializados',
};

export default es;
